import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, RouterStateSnapshot } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import environment from '../../../environments/environment';
import { LegFiJwtService } from '../../services/auth/legfi-jwt.service';
import { JwtLegFiClaims } from '../../services/auth/jwt-legfi-claims.model';
import { MembersService } from '../../services/members/members.service';
import { TwoFactorGateDialogComponent } from '../../components/v2-layout/shared/two-factor-gate-dialog/two-factor-gate-dialog.component';
import { TwoFactorGateReason } from '../../enums/two-factor-gate-reason.enum';

/** Security page where either audience finishes 2FA enrolment. */
const SECURITY_ROUTE = '/app/settings/user/security';
/** Sign-in route for the Treasury hard-gate "Back to login" escape. */
const LOGIN_ROUTE = '/auth/login';

/**
 * Mandatory-2FA access guard for two distinct audiences:
 *
 * 1. **Super users (SD-5787).** PayHOA super-admin staff must have 2FA enrolled before reaching the
 *    super-admin panel. An un-enrolled super user is redirected to the Security page to set it up.
 *
 * 2. **Treasury required users (SD-8025 enforcement / SD-8102 UX / SD-8330 fix).** Decided
 *    server-side via the `mfaRequired` JWT claim (financial-account write-access users and spend
 *    cardholders; super users are exempt from this claim, so the two audiences never overlap). The
 *    claim already narrows to un-enrolled users in *operational*-Treasury orgs (sticky flag +
 *    financial-account row), so the guard trusts it and holds the user on the shared `disableClose`
 *    gate dialog until they enrol — no client-side connect-status probe (that fail-opened for
 *    cardholders, see `_enforceTreasury2fa`). The `mfaAudience` claim selects the gate's reasoning
 *    copy (write user vs cardholder). The server middleware remains the authoritative money-movement
 *    gate; this guard is the front-door UX.
 *
 * The super-user path (1) is production-only. The Treasury path (2) gates whenever the SERVER
 * enforces — driven by the `mfaEnforced` claim (config('app.enforce_mandatory_2fa_gate') / env
 * MANDATORY_2FA_GATE_ENFORCED), NOT environment.production — so the client dialog and the server
 * middleware align in every environment off the one switch (SD-8330).
 *
 * Impersonating-support sessions are exempt from the Treasury path (2), mirroring the server
 * middleware's own exemption (SD-9545). Not from the super-user path (1), which is unreachable under
 * impersonation anyway: the token is minted from the target, so `superUser` is the target's flag.
 */
@Injectable({
    providedIn: 'root',
})
export class SuperUser2faGuard implements CanActivate, CanActivateChild
{
    private _twoFactorEnabled: boolean | null = null;

    constructor(
            private _router: Router,
            private _membersService: MembersService,
            private _dialog: MatDialog,
    ) {}

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | boolean {
        const jwt = LegFiJwtService.read();
        if (!jwt) {
            return true;
        }

        // Already confirmed enrolled this session — no need to re-gate either audience.
        if (this._twoFactorEnabled === true) {
            return true;
        }

        // Always allow the Security page so either audience can finish enrolment there.
        if (state.url.startsWith(SECURITY_ROUTE)) {
            return true;
        }

        // Super-user mandatory 2FA (SD-5787): a separate control, still production-only.
        if (jwt.superUser) {
            return environment.production ? this._enforceSuperUser2fa(jwt) : true;
        }

        // Treasury mandatory 2FA (SD-8025 / SD-8102 / SD-8330): gate exactly when the SERVER enforces.
        // `mfaEnforced` mirrors config('app.enforce_mandatory_2fa_gate') (env MANDATORY_2FA_GATE_ENFORCED),
        // so the single server switch drives the client dialog in every environment — no
        // environment.production coupling. `mfaRequired` already narrows to un-enrolled verified-Treasury
        // users, so just open the gate; no client-side Connect check.
        //
        // An impersonating support session is EXEMPT, mirroring the server (SD-9545). The impersonation
        // token is minted from the TARGET user, so it carries that user's `mfaRequired`/`mfaEnforced`;
        // reading only those two put support behind a `disableClose` enrolment wall that
        // `EnforceMandatoryTwoFactor` would have let straight through
        // (`if (property_exists($claims, 'impersonatedBy')) { return $next($request); }`). Support
        // cannot clear that wall — they do not have the customer's authenticator, and enrolling would
        // write a 2FA method onto the customer's own account. Since SD-8082 left impersonation as the
        // only recovery path for a gated, un-enrolled user, the divergence closed the only route out.
        //
        // ⚠️ ON THE CLIENT THIS CLAIM IS TRUSTED, NOT PROVEN, and the exemption is safe for a different
        // reason than that. `LegFiJwtService.read()` base64-decodes the payload and checks only `exp`
        // and `nbf` — it verifies NO signature — so someone editing their own browser storage can add
        // this marker and skip this dialog. That is not a hole this condition opens: the same person
        // could already clear `mfaRequired` instead, and per the class docblock above this guard is
        // front-door UX, never the control.
        //
        // What makes it safe is the SERVER, independently: `EnforceMandatoryTwoFactor` gates every
        // request on a *validly signed* token, and a locally edited payload fails that signature check,
        // so a forger gets past this dialog into an app shell whose API calls all 401. Nothing is
        // reachable that was not already reachable.
        //
        // ⚠️ DO NOT REUSE THIS EXEMPTION WHERE THE SERVER IS NOT THE BACKSTOP. It is only sound here
        // because the server enforces the same rule and exempts the same sessions.
        //
        // Server-side, the claim IS meaningful, which is what makes mirroring the server correct:
        // `impersonatedBy` is merged in only behind `if ($impersonator)` at every mint site in
        // JWTService, so a server-issued ordinary token does not carry it; `fromAccountNumber()` throws
        // when the target is a super, so its presence implies a real super impersonating a non-super;
        // and id 0 is unmintable because that same guard is falsy at 0. Truthiness matches how every
        // other reader of this claim in the app treats it.
        if (jwt.mfaRequired && jwt.mfaEnforced && !jwt.impersonatedBy) {
            return this._enforceTreasury2fa(jwt);
        }

        return true;
    }

    canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | boolean {
        return this.canActivate(childRoute, state);
    }

    clearCache(): void {
        this._twoFactorEnabled = null;
    }

    /**
     * Super-user path (SD-5787): an un-enrolled super user is redirected to the Security page to set
     * up 2FA before continuing. A cross-org super session with no memberId can't be read, so it's
     * allowed through (matches the original control).
     */
    private _enforceSuperUser2fa(jwt: JwtLegFiClaims): Observable<boolean> {
        if (!jwt.memberId) {
            return of(true);
        }

        return this._membersService.getMember(jwt.orgId, jwt.memberId).pipe(
                map((membership) => {
                    const enrolled = membership.user.isGoogleAuthVerificationEnrolled
                            || membership.user.isSmsAuthVerificationEnrolled;
                    this._twoFactorEnabled = enrolled;

                    if (enrolled) {
                        return true;
                    }

                    void this._router.navigate([SECURITY_ROUTE]);
                    return false;
                }),
                catchError(() => of(true)),
        );
    }

    /**
     * Treasury path (SD-8025 / SD-8102 / SD-8330): the server `mfaRequired` claim already
     * establishes that this is an un-enrolled required user in an *operational*-Treasury org
     * (sticky flag + financial-account gate — see `User::requiresMandatoryTwoFactor`), so we open
     * the hard login gate directly and hold navigation until enrolment completes.
     *
     * No client-side connect-status probe: the previous version waited for a `connect/status`
     * read to report VERIFIED, but that endpoint 403s for a cardholder with no financial-account
     * read permission, so the status never resolved and the guard fell through to "allow" — a
     * FAIL-OPEN that let un-enrolled cardholders past the gate while the server kept 403-ing their
     * data, 403-looping them with no way to enrol (SD-8330). The audience-specific copy is chosen
     * from the `mfaAudience` claim.
     */
    private _enforceTreasury2fa(jwt: JwtLegFiClaims): Observable<boolean> {
        const reason = jwt.mfaAudience === 'cardholder'
                ? TwoFactorGateReason.TREASURY_LOGIN_CARDHOLDER
                : TwoFactorGateReason.TREASURY_LOGIN;

        // Hard login gate: disableClose, so it only resolves `true` once the user has enrolled.
        return TwoFactorGateDialogComponent
                .open(this._dialog, reason)
                .afterClosed()
                .pipe(
                        map((result) => {
                            const nowEnrolled = result === true;
                            this._twoFactorEnabled = nowEnrolled;

                            if (!nowEnrolled) {
                                // User cancelled the hard gate — clear the login attempt and return
                                // to sign-in (an escape hatch, no self-service 2FA recovery yet, SD-8082).
                                this._backToLogin();
                            }

                            return nowEnrolled;
                        }),
                );
    }

    /**
     * Clear the login attempt and hard-redirect to sign-in. A full-page redirect
     * (not `Router.navigate`) is used deliberately: navigating via the router while
     * this guard is still resolving re-enters the router, and the JWT is cleared
     * statically so the guard needn't depend on AuthService — both were the cause of
     * the earlier failure.
     */
    private _backToLogin(): void {
        LegFiJwtService.clear();
        window.location.href = LOGIN_ROUTE;
    }
}
